import { Account } from 'shared';
import { inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  Auth,
  authState,
  createUserWithEmailAndPassword as fbCreateUserEmail,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  User
} from '@angular/fire/auth';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _auth = inject(Auth);

  $user: Observable<User> = new BehaviorSubject(undefined);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    if (isPlatformBrowser(this.platformId)) {
      this.$user = authState(this._auth);
    }
  }

  async createUserWithEmailAndPassword(account: Account, password: string) {
    await fbCreateUserEmail(this._auth, account.email, password);
    const currentUser = await this.getCurrentUser();
    sendEmailVerification(currentUser);
    return updateProfile(currentUser, {
      displayName: account.displayName
    });
  }

  async sendEmailVerification() {
    const currentUser = await this.getCurrentUser();
    return sendEmailVerification(currentUser);
  }

  loginWithEmail(email: string, password: string) {
    return signInWithEmailAndPassword(this._auth, email, password);
  }

  logout() {
    return signOut(this._auth);
  }

  requestEmailPasswordLink(email: string) {
    return sendPasswordResetEmail(this._auth, email);
  }

  async refreshUser() {
    const currentUser = await this.getCurrentUser();
    if (currentUser) {
      await currentUser.getIdToken(true);
      await currentUser.reload();
    }
  }

  async getIdTokenResult(refresh?: boolean) {
    const currentUser = await this.getCurrentUser();
    return currentUser.getIdTokenResult(refresh);
  }

  async getUserRoles(refresh: boolean = true): Promise<string[]> {
    const user = await this.getCurrentUser();
    const emptyTok = {
      claims: {
        roles: [],
        status: ''
      }
    } as any;
    const token = user ? await this.getIdTokenResult(refresh) : emptyTok;
    return (token.claims.roles as string[]) || [];
  }

  async userHasRole(roleName: string): Promise<boolean> {
    return (await this.getUserRoles()).some((r) => {
      return r === roleName;
    });
  }

  resetUserPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this._auth, email);
  }

  getCMSRoleOptions() {
    return ['admin', 'inventory-admin', 'shop-admin', 'cms-user', 'design-admin'];
  }

  getCurrentUser(): Promise<User> {
    return authState(this._auth).pipe(take(1)).toPromise();
  }
}
